import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';

const initialState = {
  byReport: {},
  loading: false,
  syncing: false,
  error: null,
};

const rejectWith = (rejectWithValue, reportType, error, fallback) => {
  if (error?.response?.data) {
    return rejectWithValue({ reportType, error: error.response.data.error || error.response.data });
  }
  return rejectWithValue({ reportType, error: fallback });
};

export const getReportSheetSync = createAsyncThunk(
  'reportSheetSync/get',
  async (reportType, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`/api/v1/reports/${reportType}/sheet-sync`);
      return { reportType, data };
    } catch (error) {
      return rejectWith(rejectWithValue, reportType, error, 'Failed to load Google Sheets destinations');
    }
  }
);

export const addReportSheetDestination = createAsyncThunk(
  'reportSheetSync/add',
  async ({ reportType, spreadsheet, tabName, columns }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`/api/v1/reports/${reportType}/sheet-sync`, { spreadsheet, tabName, columns });
      return { reportType, data };
    } catch (error) {
      return rejectWith(rejectWithValue, reportType, error, 'Failed to add Google Sheets destination');
    }
  }
);

export const updateReportSheetDestination = createAsyncThunk(
  'reportSheetSync/update',
  async ({ reportType, destinationId, enabled, columns }, { rejectWithValue }) => {
    try {
      const { data } = await axios.patch(`/api/v1/reports/${reportType}/sheet-sync/${destinationId}`, { enabled, columns });
      return { reportType, data };
    } catch (error) {
      return rejectWith(rejectWithValue, reportType, error, 'Failed to update Google Sheets destination');
    }
  }
);

export const removeReportSheetDestination = createAsyncThunk(
  'reportSheetSync/remove',
  async ({ reportType, destinationId }, { rejectWithValue }) => {
    try {
      await axios.delete(`/api/v1/reports/${reportType}/sheet-sync/${destinationId}`);
      return { reportType, destinationId };
    } catch (error) {
      return rejectWith(rejectWithValue, reportType, error, 'Failed to remove Google Sheets destination');
    }
  }
);

export const runReportSheetSync = createAsyncThunk(
  'reportSheetSync/run',
  async ({ reportType, destinationId = null, handlerParams = {} }, { rejectWithValue }) => {
    try {
      const url = destinationId
        ? `/api/v1/reports/${reportType}/sheet-sync/${destinationId}/run`
        : `/api/v1/reports/${reportType}/sheet-sync/run`;
      const { data } = await axios.post(url, { handlerParams });
      return { reportType, data };
    } catch (error) {
      return rejectWith(rejectWithValue, reportType, error, 'Google Sheets sync failed');
    }
  }
);

const ensureReport = (state, reportType) => {
  if (!state.byReport[reportType]) {
    state.byReport[reportType] = {
      destinations: [],
      history: [],
      columns: [],
      isConfigured: false,
      serviceAccountEmail: null,
    };
  }
  return state.byReport[reportType];
};

export const reportSheetSync = createSlice({
  name: 'reportSheetSync',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getReportSheetSync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getReportSheetSync.fulfilled, (state, action) => {
        state.loading = false;
        const { reportType, data } = action.payload;
        state.byReport[reportType] = {
          destinations: data?.destinations || [],
          history: data?.history || [],
          columns: data?.columns || [],
          isConfigured: Boolean(data?.isConfigured),
          serviceAccountEmail: data?.serviceAccountEmail || null,
        };
      })
      .addCase(getReportSheetSync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.error || action.error?.message || 'Failed to load Google Sheets destinations';
      })
      .addCase(addReportSheetDestination.fulfilled, (state, action) => {
        const { reportType, data } = action.payload;
        const entry = ensureReport(state, reportType);
        if (data?.destination) entry.destinations = [...entry.destinations, data.destination];
      })
      .addCase(updateReportSheetDestination.fulfilled, (state, action) => {
        const { reportType, data } = action.payload;
        const entry = ensureReport(state, reportType);
        if (data?.destination) {
          entry.destinations = entry.destinations.map((d) => (d.id === data.destination.id ? data.destination : d));
        }
      })
      .addCase(removeReportSheetDestination.fulfilled, (state, action) => {
        const { reportType, destinationId } = action.payload;
        const entry = ensureReport(state, reportType);
        entry.destinations = entry.destinations.filter((d) => d.id !== destinationId);
      })
      .addCase(runReportSheetSync.pending, (state) => {
        state.syncing = true;
      })
      .addCase(runReportSheetSync.fulfilled, (state, action) => {
        state.syncing = false;
        const { reportType, data } = action.payload;
        const entry = ensureReport(state, reportType);
        if (Array.isArray(data?.history)) entry.history = data.history;
      })
      .addCase(runReportSheetSync.rejected, (state) => {
        state.syncing = false;
      });
  },
});

export default reportSheetSync.reducer;
